// PRD.330 — Generic report SQL compiler (PowerSync / local SQLite executor).
//
// Compiles ONE applied `ReportQuery` into parameterized SQLite for rows,
// count/aggregates, and facets. Every variant shares the same WHERE builder,
// so rows, matching count, totals, and facet options can never disagree about
// which rows match (PRD.330 §15.4).
//
// SAFETY (PRD.330 §26):
//   - every user value (search, filters, dates, paging) is a bound `?` param;
//   - column ids resolve ONLY through the code-authored `fields` map — an id
//     the definition doesn't know throws before any SQL is built;
//   - sort direction maps to the literal ASC/DESC keywords only;
//   - LIKE input is escaped (`\`, `%`, `_`) with ESCAPE '\'.
//
// DATES (PRD.330 §13.3): stored timestamps are compared through julianday(),
// never as text. Locally written rows use `Date.toISOString()`
// ('YYYY-MM-DDTHH:mm:ss.sssZ'); rows replicated by PowerSync arrive as
// 'YYYY-MM-DD HH:mm:ss[.fff…]Z'. Text comparison of those two shapes is wrong
// near day boundaries; julianday() parses both.

import { effectiveSort, validateReportQuery } from '@helios/domain';
import type {
  ReportColumnFilter,
  ReportDefinition,
  ReportQuery,
  ReportUtcBounds,
} from '@helios/domain';

export interface ReportFieldSql {
  /** SQL expression for filtering/search on this column. Code-authored only. */
  expr: string;
  /** SQL expression for ORDER BY (defaults to `expr`). */
  sortExpr?: string;
  /** Sort NULLs after non-NULLs in both directions. */
  nullsLast?: boolean;
}

export interface ReportSqlSource<C extends string> {
  definition: ReportDefinition<C>;
  /** FROM clause body (tables + joins), without the FROM keyword. */
  from: string;
  /** Raw stored timestamp column for the global date range, e.g. 'ip.paid_at'. */
  dateColumn: string;
  fields: Record<C, ReportFieldSql>;
  /** Deterministic tiebreak expressions appended after the primary sort, same direction. */
  tiebreak: ReadonlyArray<string>;
}

export interface CompiledReportSql {
  sql: string;
  params: unknown[];
}

export class ReportQueryValidationError extends Error {
  readonly codes: string[];
  constructor(codes: string[]) {
    super(`Invalid report query: ${codes.join(', ')}`);
    this.name = 'ReportQueryValidationError';
    this.codes = codes;
  }
}

/** Escape LIKE metacharacters so user input matches literally (use with ESCAPE '\'). */
export function escapeLike(term: string): string {
  return term.replace(/[\\%_]/g, (ch) => `\\${ch}`);
}

const LIKE = "LIKE ? ESCAPE '\\'";

function containsParam(value: string): string {
  return `%${escapeLike(value.trim())}%`;
}

/** Throws unless the query passes domain validation AND every id maps to SQL. */
export function assertValidReportQuery<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
): void {
  const errors: string[] = validateReportQuery(source.definition, query).map((e) => e.code);
  for (const column of source.definition.columns) {
    if (!Object.prototype.hasOwnProperty.call(source.fields, column.id)) {
      errors.push('unmapped_column');
    }
  }
  if (errors.length > 0) throw new ReportQueryValidationError(errors);
}

function fieldFor<C extends string>(source: ReportSqlSource<C>, columnId: C): ReportFieldSql {
  if (!Object.prototype.hasOwnProperty.call(source.fields, columnId)) {
    throw new ReportQueryValidationError(['unknown_column']);
  }
  return source.fields[columnId];
}

function compileFilter(
  field: ReportFieldSql,
  filter: ReportColumnFilter,
): { sql: string; params: unknown[] } {
  switch (filter.kind) {
    case 'text':
      return { sql: `(${field.expr}) ${LIKE}`, params: [containsParam(filter.value)] };
    case 'values':
      return {
        sql: `(${field.expr}) IN (${filter.values.map(() => '?').join(', ')})`,
        params: [...filter.values],
      };
    case 'range': {
      const parts: string[] = [];
      const params: unknown[] = [];
      if (filter.min !== undefined) {
        parts.push(`(${field.expr}) >= ?`);
        params.push(filter.min);
      }
      if (filter.max !== undefined) {
        parts.push(`(${field.expr}) <= ?`);
        params.push(filter.max);
      }
      return { sql: parts.join(' AND '), params };
    }
  }
}

/**
 * The single WHERE builder. Clause order is fixed (date → search → filters in
 * definition column order) so identical queries always yield identical SQL
 * and param order. `omitFilterFor` drops exactly one column's own filter —
 * used for cascading facets.
 */
export function buildReportWhere<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  opts: { omitFilterFor?: C } = {},
): CompiledReportSql {
  const clauses: string[] = [];
  const params: unknown[] = [];

  clauses.push(
    `julianday(${source.dateColumn}) >= julianday(?) AND julianday(${source.dateColumn}) < julianday(?)`,
  );
  params.push(bounds.startUtc, bounds.endUtcExclusive);

  const term = query.search.trim();
  if (term !== '' && source.definition.searchableColumns.length > 0) {
    const exprs = source.definition.searchableColumns.map((id) => fieldFor(source, id).expr);
    clauses.push(`(${exprs.map((e) => `(${e}) ${LIKE}`).join(' OR ')})`);
    for (let i = 0; i < exprs.length; i++) params.push(containsParam(term));
  }

  for (const column of source.definition.columns) {
    if (column.id === opts.omitFilterFor) continue;
    const filter = query.filters[column.id];
    if (filter === undefined) continue;
    const compiled = compileFilter(fieldFor(source, column.id), filter);
    clauses.push(compiled.sql);
    params.push(...compiled.params);
  }

  return { sql: clauses.join('\n  AND '), params };
}

/** Allowlisted ORDER BY with a deterministic tiebreak (no params). */
export function buildReportOrderBy<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
): string {
  const sort = effectiveSort(source.definition, query);
  const column = source.definition.columns.find((c) => c.id === sort.columnId);
  if (!column || !column.sortable) throw new ReportQueryValidationError(['unsortable_column']);
  const dir = sort.direction === 'asc' ? 'ASC' : sort.direction === 'desc' ? 'DESC' : null;
  if (dir === null) throw new ReportQueryValidationError(['invalid_sort_direction']);
  const field = fieldFor(source, sort.columnId);
  const keys: string[] = [];
  if (field.nullsLast) keys.push(`(${field.expr}) IS NULL ASC`);
  keys.push(`${field.sortExpr ?? field.expr} ${dir}`);
  for (const t of source.tiebreak) keys.push(`${t} ${dir}`);
  return keys.join(', ');
}

/** One page of rows. */
export function buildReportRowsSql<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  selectList: string,
): CompiledReportSql {
  assertValidReportQuery(source, query);
  const where = buildReportWhere(source, query, bounds);
  const pageSize = source.definition.pageSize;
  const offset = (query.page - 1) * pageSize;
  return {
    sql: `SELECT ${selectList}\nFROM ${source.from}\nWHERE ${where.sql}\nORDER BY ${buildReportOrderBy(source, query)}\nLIMIT ? OFFSET ?`,
    params: [...where.params, pageSize, offset],
  };
}

/**
 * Matching count + the definition's aggregates over the FULL filtered result
 * (never a page). Columns: one per aggregate key; sums are COALESCEd to 0.
 */
export function buildReportSummarySql<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
): CompiledReportSql {
  assertValidReportQuery(source, query);
  const where = buildReportWhere(source, query, bounds);
  // `ratio` aggregates (e.g. Average Payment) have no SQL: they are derived
  // from these full-query values by `deriveReportAggregates` (@helios/domain).
  const selects = source.definition.aggregates.flatMap((agg) => {
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(agg.key))
      throw new ReportQueryValidationError(['invalid_aggregate']);
    if (agg.kind === 'ratio') return [];
    return agg.kind === 'count'
      ? [`COUNT(*) AS ${agg.key}`]
      : [`COALESCE(SUM(${fieldFor(source, agg.columnId).expr}), 0) AS ${agg.key}`];
  });
  return {
    sql: `SELECT ${selects.join(', ')}\nFROM ${source.from}\nWHERE ${where.sql}`,
    params: where.params,
  };
}

/**
 * Cascading facet for one facet column: distinct raw values + counts under
 * every applied constraint EXCEPT that column's own filter.
 */
export function buildReportFacetSql<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  columnId: C,
): CompiledReportSql {
  assertValidReportQuery(source, query);
  if (!source.definition.facetColumns.includes(columnId)) {
    throw new ReportQueryValidationError(['not_a_facet_column']);
  }
  const expr = fieldFor(source, columnId).expr;
  const where = buildReportWhere(source, query, bounds, { omitFilterFor: columnId });
  return {
    sql: `SELECT (${expr}) AS value, COUNT(*) AS count\nFROM ${source.from}\nWHERE ${where.sql}\nGROUP BY (${expr})\nORDER BY (${expr}) COLLATE NOCASE ASC`,
    params: where.params,
  };
}

/**
 * PRD.330 Phase 4 — grouped projection for a category-bar visualization.
 *
 * Applies EVERY applied constraint — date, search and all column filters,
 * INCLUDING a filter on its own dimension (unlike a facet, which omits its
 * own column). So the chart describes exactly the rows the summary counts.
 * No ORDER BY / LIMIT / OFFSET: sort and pagination never affect it; display
 * order comes from the pure category shaping in @helios/domain.
 * Columns: `category`, then the measure alias(es) from the definition.
 */
export function buildReportGroupedSql<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  visualizationId: string,
): CompiledReportSql {
  assertValidReportQuery(source, query);
  const viz = source.definition.visualizations?.find((v) => v.id === visualizationId);
  if (!viz || viz.kind !== 'categoryBar') {
    throw new ReportQueryValidationError(['unknown_visualization']);
  }
  const isColumn = (id: C) => source.definition.columns.some((c) => c.id === id);
  if (!isColumn(viz.dimension) || !isColumn(viz.measure.columnId)) {
    throw new ReportQueryValidationError(['unknown_column']);
  }
  const keys = [viz.measure.key, viz.secondaryMeasure?.key].filter(
    (k): k is string => k !== undefined,
  );
  if (keys.some((k) => !/^[A-Za-z][A-Za-z0-9_]*$/.test(k) || k === 'category')) {
    throw new ReportQueryValidationError(['invalid_aggregate']);
  }
  const dimension = fieldFor(source, viz.dimension).expr;
  const measure = fieldFor(source, viz.measure.columnId).expr;
  const selects = [
    `(${dimension}) AS category`,
    `COALESCE(SUM(${measure}), 0) AS ${viz.measure.key}`,
    ...(viz.secondaryMeasure ? [`COUNT(*) AS ${viz.secondaryMeasure.key}`] : []),
  ];
  const where = buildReportWhere(source, query, bounds); // no omitFilterFor — by design
  return {
    sql: `SELECT ${selects.join(', ')}\nFROM ${source.from}\nWHERE ${where.sql}\nGROUP BY (${dimension})`,
    params: where.params,
  };
}

/** One bucket's half-open UTC bounds (from `localDayRangeToUtcBounds`). */
export interface ReportTimeBucketBounds {
  key: string;
  startUtc: string;
  endUtcExclusive: string;
}

/**
 * PRD.330 Phase 5 — time-series projection for a `timeSeries` visualization.
 *
 * Buckets are computed in TypeScript (domain `buildReportTimeBuckets` +
 * `localDayRangeToUtcBounds`) and passed as a VALUES table, so SQLite never
 * does timezone math: each payment joins the one bucket whose local-day UTC
 * bounds contain its date column (compared through julianday(), like the
 * canonical date filter). Same canonical WHERE as the summary — NO own-filter
 * omission; no ORDER BY / LIMIT / OFFSET (sort and paging never apply).
 * With no buckets (a one-day range) it returns an inert, row-less query.
 */
export function buildReportTimeSeriesSql<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  visualizationId: string,
  buckets: ReadonlyArray<ReportTimeBucketBounds>,
): CompiledReportSql {
  assertValidReportQuery(source, query);
  const viz = source.definition.visualizations?.find((v) => v.id === visualizationId);
  if (!viz || viz.kind !== 'timeSeries') {
    throw new ReportQueryValidationError(['unknown_visualization']);
  }
  const date = source.definition.columns.find((c) => c.id === viz.dateDimension);
  if (
    !date ||
    date.valueType !== 'date' ||
    !source.definition.columns.some((c) => c.id === viz.measure.columnId)
  ) {
    throw new ReportQueryValidationError(['unknown_column']);
  }
  const keys = [viz.measure.key, viz.secondaryMeasure?.key].filter(
    (k): k is string => k !== undefined,
  );
  if (keys.some((k) => !/^[A-Za-z][A-Za-z0-9_]*$/.test(k) || k === 'bucket')) {
    throw new ReportQueryValidationError(['invalid_aggregate']);
  }
  const measures = [
    `COALESCE(SUM(${fieldFor(source, viz.measure.columnId).expr}), 0) AS ${viz.measure.key}`,
    ...(viz.secondaryMeasure ? [`COUNT(*) AS ${viz.secondaryMeasure.key}`] : []),
  ];
  if (buckets.length === 0) {
    // Not applicable (one-day range): a valid, row-less query with the same columns.
    const zeros = keys.map((k) => `0 AS ${k}`).join(', ');
    return { sql: `SELECT NULL AS bucket, ${zeros} WHERE 0`, params: [] };
  }
  const where = buildReportWhere(source, query, bounds); // no omitFilterFor — by design
  const values = buckets.map(() => '(?, ?, ?)').join(', ');
  const bucketParams = buckets.flatMap((b) => [b.key, b.startUtc, b.endUtcExclusive]);
  const col = source.dateColumn;
  return {
    sql:
      `WITH report_buckets(bucket, start_utc, end_utc) AS (VALUES ${values})\n` +
      `SELECT rb.bucket AS bucket, ${measures.join(', ')}\n` +
      `FROM ${source.from}\n` +
      `JOIN report_buckets rb ON julianday(${col}) >= julianday(rb.start_utc) AND julianday(${col}) < julianday(rb.end_utc)\n` +
      `WHERE ${where.sql}\n` +
      `GROUP BY rb.bucket`,
    params: [...bucketParams, ...where.params],
  };
}

/**
 * Stable cache key fragments. Rows depend on the whole query; summary and
 * facets must NOT depend on `page` (paging never re-runs aggregates), and a
 * facet must not depend on its own column's filter.
 */
export function reportQueryKeyParts<C extends string>(
  source: ReportSqlSource<C>,
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
  opts: { includePage: boolean; includeSort: boolean; omitFilterFor?: C },
): string {
  const filters: Array<[string, ReportColumnFilter]> = [];
  for (const column of source.definition.columns) {
    const f = query.filters[column.id];
    if (f !== undefined && column.id !== opts.omitFilterFor) filters.push([column.id, f]);
  }
  return JSON.stringify({
    bounds,
    search: query.search.trim(),
    filters,
    ...(opts.includeSort ? { sort: effectiveSort(source.definition, query) } : {}),
    ...(opts.includePage ? { page: query.page } : {}),
  });
}
